import { ConfirmDialog, EmptyState, SkeletonList, StatusChip, isolate } from '@batiflow/ui';
import { Button } from '@fluentui/react-components';
import { People24Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { useNotify } from '@/shared/hooks/use-notify';
import { isClosed, isStepUpRequired, type Space } from '../model';
import { useExtendMember, useRemoveMember, useSpaceMembers } from './api';
import {
  canExtend,
  canRevokeMember,
  dateText,
  scopeSummary,
  sharingErrorMessage,
  type SpaceMember,
} from './model';
import { LoadState, RowList, SharingRow } from './sharing-ui';

export interface MembersPanelProps {
  space: Space;
  /** Only the delegates of this partnership (a partner admin); absent: everyone with access. */
  partnerId?: string;
  /** Offers "Prolonger de 90 jours" on a guest (the people who manage the space). */
  allowExtend: boolean;
  /** The empty state's texts (differ for the host and for a partner admin). */
  emptyTitle: string;
  emptyBody: string;
  /** Whether the panel names the organisation of each person (the host's list does). */
  showOrganisation: boolean;
}

/**
 * The people with access to a space, one row each: name, organisation, how they got in, rights and
 * end date. "Retirer" appears where the server allows it (`can_revoke`), "Prolonger de 90 jours"
 * on a guest for the people who manage the space.
 */
export function MembersPanel({
  space,
  partnerId,
  allowExtend,
  emptyTitle,
  emptyBody,
  showOrganisation,
}: MembersPanelProps) {
  const { t } = useTranslation('spaces');
  const { t: tCommon } = useTranslation('common');
  const locale = useLocale();
  const notify = useNotify();
  const closed = isClosed(space);
  const members = useSpaceMembers(space.id, partnerId);
  const remove = useRemoveMember(space.id);
  const extend = useExtendMember(space.id);
  const [removing, setRemoving] = useState<SpaceMember | null>(null);

  const reportError = (error: unknown) => {
    if (isStepUpRequired(error)) return;
    notify({
      intent: 'error',
      message: sharingErrorMessage(t, tCommon, error, { forbidden: 'sharing.errors.forbidden' }),
    });
  };

  const extendAccess = (member: SpaceMember) => {
    extend.mutate(
      { spaceId: space.id, memberId: member.id },
      {
        onSuccess: (result) => {
          notify({
            intent: 'success',
            message: t('sharing.people.extended', {
              name: isolate(member.displayName),
              date: dateText(result.expiresAt, locale),
            }),
          });
        },
        onError: reportError,
      },
    );
  };

  if (members.isError && !members.data) {
    return (
      <LoadState
        title={t('sharing.people.loadFailedTitle')}
        body={t('sharing.loadFailedBody')}
        onRetry={() => {
          void members.refetch();
        }}
      />
    );
  }
  if (!members.data) return <SkeletonList count={4} />;
  if (members.data.items.length === 0) {
    return (
      <EmptyState
        headingLevel={3}
        illustration={<People24Regular />}
        title={emptyTitle}
        description={emptyBody}
      />
    );
  }

  return (
    <>
      <RowList label={t('sharing.people.listLabel')}>
        {members.data.items.map((member) => (
          <SharingRow
            key={member.id}
            title={
              <>
                <bdi>{member.displayName}</bdi>
                <StatusChip label={t(`sharing.memberVia.${member.via}`)} intent="neutral" />
              </>
            }
            details={[
              ...(showOrganisation && member.organizationName
                ? [t('sharing.people.organisation', { name: isolate(member.organizationName) })]
                : []),
              scopeSummary(member.scope, t),
              t('access.rights', { rights: t(`rights.${member.rights}`) }),
              member.expiresAt
                ? t('access.until', { date: dateText(member.expiresAt, locale) })
                : t('access.noEnd'),
            ]}
            actions={
              closed ? null : (
                <>
                  {allowExtend && canExtend(member) ? (
                    <Button
                      appearance="secondary"
                      disabledFocusable={extend.isPending}
                      aria-label={t('sharing.people.extendLabel', {
                        name: isolate(member.displayName),
                      })}
                      onClick={() => {
                        extendAccess(member);
                      }}
                    >
                      {t('sharing.people.extend')}
                    </Button>
                  ) : null}
                  {canRevokeMember(member) ? (
                    <Button
                      appearance="secondary"
                      aria-label={t('sharing.people.removeLabel', {
                        name: isolate(member.displayName),
                      })}
                      onClick={() => {
                        setRemoving(member);
                      }}
                    >
                      {t('sharing.remove')}
                    </Button>
                  ) : null}
                </>
              )
            }
          />
        ))}
      </RowList>
      <ConfirmDialog
        open={removing !== null}
        destructive
        title={t('sharing.people.removeTitle', { name: isolate(removing?.displayName ?? '') })}
        body={t('sharing.people.removeBody')}
        confirmLabel={t('sharing.remove')}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        onConfirm={async () => {
          if (!removing) return;
          await remove.mutateAsync({ spaceId: space.id, memberId: removing.id });
          notify({
            intent: 'success',
            message: t('sharing.people.removed', { name: isolate(removing.displayName) }),
          });
        }}
        onError={reportError}
      />
    </>
  );
}
